import getClientConfig from "./getClientConfig";
import Logger, { LOG_LEVELS } from "./logger";
import { generateRandomCodeVerifier, calculatePKCECodeChallenge, generateRandomState } from "oauth4webapi";
import { jwtDecode } from "jwt-decode";
import { setAccessToken, getAccessToken, setRefreshToken, getRefreshToken, setIdToken } from './tokenStorage';

class AuthService {
    constructor() {
        const { aiClientId, aiTokenUrl, guiUrl, aiAuthorizationUrl, aiEndSessionUrl, enableIamAuth } = getClientConfig();
        let scope = 'openid profile email offline_access avatax avatax_api';
        if (enableIamAuth) {
            scope = `${scope} iam-ds vat-reporting`;
        }
        this.authConfig = {
            clientId: aiClientId,
            redirectUri: `${guiUrl}/auth/callback`,
            authorizationUrl: aiAuthorizationUrl,
            tokenEndpoint: aiTokenUrl,
            endSessionUrl: aiEndSessionUrl,
            scope: scope
        };

        this.log = new Logger("AUTH_SERVICE", LOG_LEVELS.DEBUG);
    }

    async login(options = {}) {
        const codeVerifier = generateRandomCodeVerifier();
        const codeChallenge = await calculatePKCECodeChallenge(codeVerifier);

        sessionStorage.setItem('code_verifier', codeVerifier);

        // Allow overriding origin path/query (e.g., when retrying after sessionStorage loss)
        const path = options.pathOverride ?? window.location.pathname;
        const queryStringSource = options.queryOverride ?? window.location.search;
        const queryString = queryStringSource?.startsWith('?')
            ? queryStringSource.slice(1)
            : queryStringSource ?? '';

        const state = `${generateRandomState()}::path::${path}::query::${queryString}::end`;
        sessionStorage.setItem('auth_state', state);

        // Build Authorization URL
        const params = new URLSearchParams({
            client_id: this.authConfig.clientId,
            redirect_uri: this.authConfig.redirectUri,
            response_type: 'code',
            scope: this.authConfig.scope,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            state: state
        });

        // Mark an auth retry if explicitly requested by caller
        if (options.markAsRetry === true) {
            const current = parseInt(localStorage.getItem('auth_auto_retry_count') || '0', 10) || 0;
            localStorage.setItem('auth_auto_retry_count', String(current + 1));
        }

        window.location.href = `${this.authConfig.authorizationUrl}?${params.toString()}`;
    }

    async handleLogin() {
        try {
            await authService.login();
        } catch (error) {
            this.log.warn('Login failed:', error);
        }
    };

    async handleRedirect(code, state) {
        try {
            // Verify presence of state and PKCE material first
            const savedState = sessionStorage.getItem('auth_state');
            const codeVerifier = sessionStorage.getItem('code_verifier');

            if (!savedState || !codeVerifier) {
                // sessionStorage was likely cleared (e.g., password reset flow). Attempt a single auto-retry.
                const retryCount = parseInt(localStorage.getItem('auth_auto_retry_count') || '0', 10) || 0;
                if (retryCount < 1) {
                    const { path, query } = this.extractPathAndQueryFromState(state);
                    // Will navigate away; mark this as a retry to avoid loops
                    await this.login({ pathOverride: path, queryOverride: query, markAsRetry: true });
                    return; // navigation will occur
                }

                // Already retried once; fail safely
                throw new Error(!savedState ? 'Invalid state parameter' : 'No code verifier found');
            }

            // Validate state to prevent CSRF
            if (state !== savedState) {
                throw new Error('Invalid state parameter');
            }

            const response = await fetch(this.authConfig.tokenEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: new URLSearchParams({
                    grant_type: 'authorization_code',
                    client_id: this.authConfig.clientId,
                    code_verifier: codeVerifier,
                    code: code,
                    redirect_uri: this.authConfig.redirectUri
                })
            });

            if (!response.ok) {
                throw new Error('Token exchange failed');
            }

            const tokens = await response.json();

            // Clean up session and retry flags
            sessionStorage.removeItem('code_verifier');
            sessionStorage.removeItem('auth_state');
            localStorage.removeItem('auth_auto_retry_count');

            return tokens;
        }
        catch (error) {
            this.log.warn('Callback handling failed:', error);
            throw error;
        }
    }

    // Private helpers
    extractPathAndQueryFromState(state) {
        try {
            if (typeof state !== 'string') {
                return { path: '/', query: '' };
            }
            const pathMatch = state.match(/::path::(.*?)::query::/);
            const queryMatch = state.match(/::query::(.*?)::end/);
            const path = pathMatch ? pathMatch[1] : '/';
            const query = queryMatch ? queryMatch[1] : '';
            return { path, query };
        } catch (_e) {
            return { path: '/', query: '' };
        }
    }

    async refreshToken() {
        try {
            const refreshToken = getRefreshToken();
            if (refreshToken == null) {
                this.log.warn('Unauthorized - Refresh token not found');
                throw new Error('Unauthorized - Refresh token not found');
            }

            const response = await fetch(this.authConfig.tokenEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: new URLSearchParams({
                    grant_type: 'refresh_token',
                    client_id: this.authConfig.clientId,
                    refresh_token: refreshToken
                })
            });

            if (response.status != 200) {
                this.log.warn('Error fetching token: Status', response.status);
                throw new Error('Error fetching token: Status ' + response.status);
            }

            const data = await response.json();
            if (!data.access_token || !data.refresh_token) {
                this.log.warn('Tokens not received');
                throw new Error('Tokens not received');
            }

            setAccessToken(data.access_token);
            setRefreshToken(data.refresh_token);
            setIdToken(data.id_token);
            return data.access_token;
        }
        catch (error) {
            this.log.warn('Error refreshing token:', error);
            throw error;
        }
    }

    timeToExpire() {
        const accessToken = getAccessToken();

        if (accessToken == null) {
            return 0;
        }

        const decoded = jwtDecode(accessToken);
        const currentTimeSeconds = Math.floor(Date.now() / 1000);
        return decoded.exp - currentTimeSeconds;
    }
}

export const authService = new AuthService();